// src/components/CreateSequenceForm.js

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Box
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useAuth } from "../util/auth";
import { createSequence, upsertTagsForEntity } from "../util/db";

function CreateSequenceForm() {
  const auth = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formAlert, setFormAlert] = useState(null);

  const { register, handleSubmit, errors, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setFormAlert(null);

    try {
      if (auth.user.role !== "in_good_standing") {
        throw new Error("You must be in good standing to add sequences.");
      }

      // 1) Convert comma-separated prompt IDs
      const promptIds = parseCommaSeparated(data.prompts);
      if (promptIds.length === 0) {
        throw new Error("Please provide at least one prompt ID.");
      }

      // 2) Create the sequence
      const [newSeq] = await createSequence({
        owner: auth.user.uid,
        prompts: promptIds,
      });

      // 3) Handle tags
      const tags = parseCommaSeparated(data.tags);
      if (tags.length > 50) {
        throw new Error("Cannot exceed 50 tags");
      }

      await upsertTagsForEntity({
        entityType: "sequence",
        entityId: newSeq.id,
        tags,
      });

      reset();
    } catch (error) {
      setFormAlert(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {formAlert && (
        <Box mb={2}>
          <Alert severity="error">{formAlert}</Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Prompt IDs (comma-separated)"
            name="prompts"
            variant="outlined"
            size="small"
            fullWidth
            error={!!errors.prompts}
            helperText={errors.prompts && errors.prompts.message}
            inputRef={register}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Tags (comma-separated)"
            name="tags"
            variant="outlined"
            size="small"
            fullWidth
            inputRef={register}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={22} /> : "Create Sequence"}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

function parseCommaSeparated(text = "") {
  return text
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

export default CreateSequenceForm;
